import type { ProviderCancellation } from "@/lib/provider-cancellation";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useSession } from "@/services/auth0/session";
import type * as z from "zod";
import type { Order } from "@/api.types.v2";
import {
  createOrderSchema as orderSchema,
  type CreateOrderSchema,
} from "@/api.schemas";
import { taskQueries } from "./tasks";
import type {
  PickupRefundRequestStatus,
  RefundChargeType,
  RefundLineApproverType,
  RefundLineStatus,
} from "./pickup-refund-requests";

// Re-export from api.schemas for backward compatibility
export const createOrderSchema = orderSchema;
export type { CreateOrderSchema } from "@/api.schemas";

interface OrdersResponse {
  data: Order[];
  metadata: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface UseOrdersOptions {
  carrierIds?: string;
  dispatchCenterIds?: string;
  carrierId?: string;
  dispatchCenterId?: string;
}

// Get orders with pagination and filtering
export function useOrders(
  page: number,
  limit: number,
  status?: string, // Status filtering should be done at shipment level now
  search?: string,
  type?: string,
  options: UseOrdersOptions = {}
) {
  const { token } = useSession();
  const { carrierId, dispatchCenterId, carrierIds, dispatchCenterIds } =
    options;

  return useQuery<OrdersResponse>({
    queryKey: [
      "orders",
      page,
      limit,
      status,
      search,
      type,
      carrierId,
      dispatchCenterId,
      carrierIds,
      dispatchCenterIds,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (status) {
        params.append("status", status);
      }

      if (search) {
        params.append("search", search);
      }

      if (type) {
        params.append("type", type);
      }

      if (carrierIds !== undefined) {
        params.append("carrierIds", carrierIds);
      }
      if (dispatchCenterIds !== undefined) {
        params.append("dispatchCenterIds", dispatchCenterIds);
      }

      if (carrierId) {
        params.append("carrierId", carrierId);
      }

      if (dispatchCenterId) {
        params.append("dispatchCenterId", dispatchCenterId);
      }

      const { data } = await axios.get<OrdersResponse>(
        `${import.meta.env.VITE_API_URL}/api/v2/orders?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    enabled: !!token,
  });
}

// Get order by ID
export function useOrderById(orderId: string) {
  const { token } = useSession();
  return useQuery<Order>({
    queryKey: ["order", orderId],
    queryFn: async () => {
      const { data } = await axios.get<Order>(
        `${import.meta.env.VITE_API_URL}/api/v2/orders/${orderId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    enabled: !!token,
  });
}

// Create order mutation
export function useCreateOrder({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation<Order, Error, CreateOrderSchema>({
    mutationFn: async (values) => {
      const { data } = await axios.post<Order>(
        `${import.meta.env.VITE_API_URL}/api/v2/orders`,
        values,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["orders"] });
      await queryClient.invalidateQueries({
        queryKey: taskQueries.activePickupTasks(),
      });
      await queryClient.invalidateQueries({
        queryKey: taskQueries.activeDeliveryTasks(),
      });
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
}

// Print shipping labels
export function useOrderLabels() {
  const { token } = useSession();

  const printLabels = useMutation({
    mutationFn: async (orderId: string) => {
      const response = await axios.get<Blob>(
        `${import.meta.env.VITE_API_URL}/api/v2/orders/${orderId}/shipping-labels`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );
      return response.data;
    },
  });

  const printBulkLabels = useMutation({
    mutationFn: async (orderIds: string[]) => {
      const response = await axios.post<Blob>(
        `${import.meta.env.VITE_API_URL}/api/v2/orders/bulk-shipping-labels`,
        { orderIds },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );
      return response.data;
    },
  });

  return { printLabels, printBulkLabels };
}

export const updateOrderSchema = createOrderSchema.partial();
export type UpdateOrderSchema = z.infer<typeof updateOrderSchema>;

// Update order mutation
export function useUpdateOrder() {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation<
    Order,
    Error,
    { orderId: string; data: UpdateOrderSchema }
  >({
    mutationFn: async ({ orderId, data }) => {
      const { data: responseData } = await axios.patch<Order>(
        `${import.meta.env.VITE_API_URL}/api/v2/orders/${orderId}`,
        data,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return responseData;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}

// Search type options
export type OrderSearchType =
  | "all"
  | "order"
  | "shipment"
  | "tracking"
  | "phone";

// Define type for search results
export interface OrderSearchResult {
  id: string;
  orderNumber: string;
  createdAt: string;
  // Payment info
  paymentStatus?: string;
  totalCents?: number;
  paidAmountCents?: number;
  // Sender (FROM) info
  senderName?: string;
  senderPhone?: string;
  senderEmail?: string;
  senderAddress?: string;
  senderCity?: string;
  // Recipient (TO) info
  recipientName?: string;
  recipientPhone?: string;
  recipientEmail?: string;
  recipientAddress?: string;
  recipientCity?: string;
  // Assignment info
  driverName?: string;
  tripNumber?: number;
  // Task statuses
  tasks?: {
    type: string;
    status: string;
    shipmentNumber?: number;
    waypointSequence?: number;
  }[];
  // POD and package info
  hasPOD?: boolean;
  packageCount?: number;
  // Shipments
  shipments?: {
    id: string;
    shipmentNumber?: number;
    trackingNumber?: string;
    status: string;
    packageCount?: number;
    tasks?: { type: string; status: string; waypointSequence?: number }[];
  }[];
}

interface OrderSearchResponse {
  data: OrderSearchResult[];
  metadata: {
    count: number;
    query: string;
    type?: string;
  };
}

// Search orders
export function useOrderSearch(
  query: string,
  type: OrderSearchType = "all",
  limit = 20,
  unfinished = true
) {
  const { token } = useSession();

  return useQuery<OrderSearchResponse>({
    queryKey: ["orderSearch", query, type, limit, unfinished],
    queryFn: async () => {
      if (!query.trim())
        return { data: [], metadata: { count: 0, query: "", type } };

      const { data } = await axios.get<OrderSearchResponse>(
        `${import.meta.env.VITE_API_URL}/api/v2/orders/search`,
        {
          params: {
            query: query.trim(),
            type,
            limit,
            unfinished: unfinished.toString(),
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Handle empty response
      if (!data?.data) {
        return { data: [], metadata: { count: 0, query: query.trim(), type } };
      }

      return data;
    },
    enabled: !!token && !!query.trim(),
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

// Get pending shipment cancellation requests
export interface PendingCancellation {
  providerCancellations?: ProviderCancellation[];
  id: string;
  shipmentNumber: number;
  status: string;
  cancellationRequestedAt: string;
  cancellationRequestReason: string | null;
  sellAmountCents?: number | null;
  order?: {
    id: string;
    orderNumber: number;
    placerCustomer?: {
      firstName: string | null;
      lastName: string | null;
      email: string | null;
    };
    payments?: {
      id: string;
      amountCents: number;
      method: string;
    }[];
  };
  addresses?: {
    type: string;
    addressId: string | null;
    contactName: string | null;
    contactPhone: string | null;
    contactEmail: string | null;
    address?: {
      streetLine1: string | null;
      streetLine2: string | null;
      city: string | null;
      province: string | null;
      postalCode: string | null;
    };
  }[];
  tasks?: {
    id: string;
    type: string;
    status: string;
    contactName: string | null;
    contactPhone: string | null;
    addressLine1: string | null;
    addressCity: string | null;
    description: string | null;
    failureReason: string | null;
    failureReasonCode: number | null;
    tripId?: string | null;
    waypointId?: string | null;
    dispatchCenterId?: string | null;
    carrierId?: string | null;
  }[];
  custody:
    | { requiresDisposition: false; custody: null }
    | {
        requiresDisposition: true;
        custody:
          | {
              kind: "COMPLETED_TASK" | "RECEIPT_TASK";
              taskId: string;
              taskType: string;
              dispatchCenterId: string | null;
              carrierId: string | null;
            }
          | {
              kind: "EXTERNAL_HANDOFF";
              taskId: string;
              carrierId: string | null;
            }
          | {
              kind: "SHIPMENT_STATUS" | "PROVIDER_EVENT";
              shipmentStatus: string;
              carrierId: string | null;
            };
      };
  refundInfo?: {
    shipmentAmount: number;
    taxAmount: number;
    totalRefund: number;
  } | null;
  // Keep old fields for backward compatibility during transition
  orderNumber?: number;
  placerCustomer?: {
    id: string;
    user?: {
      firstName: string | null;
      lastName: string | null;
      email: string | null;
    };
  };
  shipments?: {
    id: string;
    status: string;
    shipmentNumber: number;
  }[];
  payments?: {
    id: string;
    amountCents: number;
    method: string;
  }[];
}

interface PendingCancellationsResponse {
  success: boolean;
  count: number;
  data: PendingCancellation[];
}

export interface UsePendingCancellationsOptions {
  carrierIds?: string;
  dispatchCenterIds?: string;
}

export function usePendingCancellations(
  options: UsePendingCancellationsOptions = {}
) {
  const { token } = useSession();
  const { carrierIds, dispatchCenterIds } = options;

  return useQuery<PendingCancellationsResponse>({
    queryKey: ["pendingCancellations", carrierIds, dispatchCenterIds],
    queryFn: async () => {
      const { data } = await axios.get<PendingCancellationsResponse>(
        `${import.meta.env.VITE_API_URL}/api/v2/shipments/pending-cancellations`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { carrierIds, dispatchCenterIds },
        }
      );
      return data;
    },
    enabled: !!token,
  });
}

// Process shipment cancellation (approve/reject)
export interface ProcessCancellationParams {
  providerReference?: string;
  confirmNotReceived?: true;
  expectedCancellationRequestedAt?: string;
  shipmentId: string;
  action: "approve" | "reject" | "confirm_receipt";
  // Ticket 08 (refund-approval-queue): "partial" deleted server-side —
  // reduction is per-component in Refund Requests.
  refundType?: "full" | "none";
  reason: string;
  notes?: string;
  // On reject: FAILED tasks to retry (re-created as PENDING; planner re-adds).
  taskIdsToRetry?: string[];
  disposition?:
    | {
        type: "HOLD_AT_DEPOT";
        dispatchCenterId: string;
        externalInterceptReference?: string;
      }
    | {
        type: "RETURN_TO_SENDER";
        returnTransportId: string;
        expectedRequestFingerprint: string;
        payer: "CUSTOMER" | "CARRIER" | "EZX" | "WAIVED";
        waivedCostBearer?: "CARRIER" | "EZX";
        costCarrierId?: string;
        externalInterceptReference?: string;
      };
}

export interface CancellationReturnOption {
  returnTransportId: string;
  requestFingerprint: string;
  quoteId: string;
  carrier: { id: string; code: string; name: string };
  carrierService: { id: string; code: string; name: string };
  labelPriceCents: number;
  taxCents: number;
  returnChargeCents: number;
  currency: "CAD";
  expiresAt: string;
  payer: string | null;
}

interface CancellationReturnOptionsResponse {
  kind: "ok" | string;
  data?: {
    options: CancellationReturnOption[];
    reason?: string;
  };
}

export function useCancellationReturnOptions() {
  const { token } = useSession();
  return useMutation<CancellationReturnOptionsResponse, Error, string>({
    mutationFn: async (shipmentId) => {
      const { data } = await axios.post<CancellationReturnOptionsResponse>(
        `${import.meta.env.VITE_API_URL}/api/v2/shipments/${shipmentId}/cancellation-return-options`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
  });
}

export function useOverrideCancellationReturnPrice() {
  const { token } = useSession();
  return useMutation<
    { success: boolean; data: { totalChargeCents: number; taxCents: number } },
    Error,
    { returnTransportId: string; effectiveNetCents: number; reason: string }
  >({
    mutationFn: async ({ returnTransportId, ...body }) => {
      const { data } = await axios.post(
        `${import.meta.env.VITE_API_URL}/api/v2/admin/return-transports/${returnTransportId}/override-price`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
  });
}

export interface CancellationRefundLineStatus {
  id: string;
  chargeType: RefundChargeType;
  approverType: RefundLineApproverType;
  status: RefundLineStatus;
  amountCents: number;
  providerCarrierId: string | null;
}

export interface CancellationRefundStatus {
  type: "refund_request";
  requestId: string;
  status: PickupRefundRequestStatus;
  amountCents: number;
  pendingProviderLines: number;
  lines: CancellationRefundLineStatus[];
}

interface ProcessCancellationResponse {
  success: boolean;
  message: string;
  shipmentId: string;
  action: "approve" | "reject";
  cancellation?: {
    tasksAction: {
      cancelled: number;
    };
    carrierAction: {
      voided: boolean;
      chitchatsId?: string;
      error?: string;
    };
  };
  refundStatus?: CancellationRefundStatus | null;
}

export function useProcessCancellation() {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation<
    ProcessCancellationResponse,
    Error,
    ProcessCancellationParams
  >({
    mutationFn: async (params) => {
      const { shipmentId, ...body } = params;
      const { data } = await axios.post<ProcessCancellationResponse>(
        `${import.meta.env.VITE_API_URL}/api/v2/shipments/${shipmentId}/process-cancellation`,
        body,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["pendingCancellations"],
      });
      await queryClient.invalidateQueries({ queryKey: ["orders"] });
      await queryClient.invalidateQueries({ queryKey: ["shipments"] });
    },
    onError: async (error) => {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        await queryClient.invalidateQueries({
          queryKey: ["pendingCancellations"],
        });
      }
    },
  });
}

// Direct search function for debugging
export async function searchOrdersDirectly(
  token: string,
  query: string,
  limit = 20
): Promise<OrderSearchResponse> {
  console.log(
    `Searching orders directly with query: ${query}, limit: ${limit}`
  );

  try {
    const response = await axios.get<OrderSearchResponse>(
      `${import.meta.env.VITE_API_URL}/api/v2/orders/search`,
      {
        params: {
          query: query.trim(),
          limit,
        },
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    console.log("Direct search response:", response);
    return response.data;
  } catch (error) {
    console.error("Error in direct search:", error);
    if (axios.isAxiosError(error) && error.response) {
      console.error("Axios error details:", {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data as Record<string, unknown>,
      });
    }

    // Return empty results with proper structure
    return {
      data: [],
      metadata: {
        count: 0,
        query: query.trim(),
      },
    };
  }
}

/** Reconcile an existing payment; this action never creates a new charge. */
export function useRecoverOrderPayment(orderId: string, shipmentId: string) {
  const { token } = useSession();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const apiUrl = import.meta.env.VITE_API_URL;
      if (!apiUrl || !token) throw new Error("Payment recovery unavailable");
      return axios.post<{ preparationStatus: string; message?: string }>(
        `${apiUrl}/api/v2/orders/${encodeURIComponent(orderId)}/recover-payment`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    },
    // Errors may follow committed settlement or preparation failure. Refresh
    // those facts even when the recovery command could not finish everything.
    onSettled: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ["shipment", shipmentId] }),
        client.invalidateQueries({ queryKey: ["shipments"] }),
        client.invalidateQueries({ queryKey: ["shipment-labels", shipmentId] }),
        client.invalidateQueries({ queryKey: ["orders"] }),
        client.invalidateQueries({ queryKey: ["order", orderId] }),
        client.invalidateQueries({ queryKey: ["order-payments", orderId] }),
        client.invalidateQueries({
          queryKey: ["order-payment-status", orderId],
        }),
        client.invalidateQueries({ queryKey: ["payments"] }),
      ]),
  });
}

/** Observe the recorded provider operation without submitting another void. */
export function useObserveProviderCancellation() {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (labelId: string) => {
      await axios.post(
        `${import.meta.env.VITE_API_URL}/api/v2/shipping/labels/${encodeURIComponent(labelId)}/void`,
        { reconciliation: { outcome: "OBSERVE" } },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: ["pendingCancellations"] }),
  });
}

/** Record explicit terminal no-effect confirmation; never submit a provider void. */
export function useConfirmProviderNoEffect() {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      labelId,
      evidence,
      expectedUpdatedAt,
    }: {
      labelId: string;
      evidence: string;
      expectedUpdatedAt: string;
    }) => {
      await axios.post(
        `${import.meta.env.VITE_API_URL}/api/v2/shipping/labels/${encodeURIComponent(labelId)}/void`,
        {
          reconciliation: {
            outcome: "TERMINAL_NO_EFFECT",
            evidence,
            expectedUpdatedAt,
            providerConfirmedRequestEndedWithoutEffect: true,
          },
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: ["pendingCancellations"] }),
  });
}
